<template>
  <div class="space-y-6">
    <!-- Cabeçalho -->
    <div class="flex items-center justify-between">
      <h1 class="text-3xl font-bold text-lwsa-black">Catálogo de APIs</h1>
      <SwaggerUpload @uploaded="refreshApis" />
    </div>

    <!-- Barra de busca e filtros -->
    <div class="bg-white p-4 rounded-lg shadow border border-lwsa-gray-200">
      <div class="flex flex-col md:flex-row gap-4">
        <div class="flex-1">
          <input 
            v-model="searchQuery"
            @input="performSearch"
            type="text" 
            placeholder="Buscar APIs..."
            class="input-field"
          >
        </div>
        <div class="flex gap-2">
          <select 
            v-model="statusFilter"
            @change="applyFilters"
            class="border border-lwsa-gray-300 rounded-lg px-3 py-2"
          >
            <option value="">Todos os Status</option>
            <option value="active">Ativo</option>
            <option value="deprecated">Depreciado</option>
            <option value="inactive">Inativo</option>
          </select>
          <select 
            v-model="sortBy"
            @change="applySorting"
            class="border border-lwsa-gray-300 rounded-lg px-3 py-2"
          >
            <option value="name">Nome</option>
            <option value="updatedAt">Última Atualização</option>
            <option value="version">Versão</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Lista de APIs -->
    <div v-if="loading" class="flex justify-center py-8">
      <LoadingSpinner />
    </div>

    <div v-else-if="filteredApis.length === 0" class="text-center py-8">
      <div class="text-lwsa-gray-500">
        <svg class="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <p class="text-lg">Nenhuma API encontrada</p>
        <p class="text-sm mt-2">Tente ajustar os filtros ou fazer upload de uma nova API</p>
      </div>
    </div>

    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <ApiCard 
        v-for="api in paginatedApis" 
        :key="api.id" 
        :api="api"
        @click="viewApi(api.id)"
        @generate-code="showCodeGenerator"
        @view-docs="viewDocumentation"
      />
    </div>

    <!-- Paginação -->
    <div v-if="totalPages > 1" class="flex justify-center items-center space-x-2">
      <button 
        @click="currentPage = Math.max(1, currentPage - 1)"
        :disabled="currentPage === 1"
        class="px-3 py-2 border border-lwsa-gray-300 rounded-lg disabled:opacity-50"
      >
        Anterior
      </button>
      
      <span class="px-4 py-2 text-sm text-lwsa-gray-600">
        Página {{ currentPage }} de {{ totalPages }}
      </span>
      
      <button 
        @click="currentPage = Math.min(totalPages, currentPage + 1)"
        :disabled="currentPage === totalPages"
        class="px-3 py-2 border border-lwsa-gray-300 rounded-lg disabled:opacity-50"
      >
        Próxima
      </button>
    </div>

    <!-- Modal de Gerador de Código -->
    <div v-if="showCodeModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg shadow-lg w-full max-w-4xl max-h-[80vh] overflow-hidden">
        <div class="flex items-center justify-between p-4 border-b border-lwsa-gray-200">
          <h3 class="text-lg font-semibold">Gerador de Código - {{ selectedApi?.name }}</h3>
          <button @click="closeCodeModal" class="text-lwsa-gray-500 hover:text-lwsa-gray-700">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div class="p-4 overflow-y-auto">
          <CodeSnippet 
            v-if="selectedApi && selectedEndpoint"
            :api="selectedApi" 
            :endpoint="selectedEndpoint" 
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import ApiCard from '@/components/api/ApiCard.vue'
import SwaggerUpload from '@/components/api/SwaggerUpload.vue'
import CodeSnippet from '@/components/api/CodeSnippet.vue'
import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
import { getAllApis, searchApis } from '@/services/apiOld'

const router = useRouter()

// Estados reativos
const apis = ref([])
const loading = ref(false)
const searchQuery = ref('')
const statusFilter = ref('')
const sortBy = ref('name')
const currentPage = ref(1)
const itemsPerPage = ref(2)

// Modal de código
const showCodeModal = ref(false)
const selectedApi = ref(null)
const selectedEndpoint = ref(null)

// Computadas
const filteredApis = computed(() => {
  let result = [...apis.value]

  // Filtro por status
  if (statusFilter.value) {
    result = result.filter(api => api.status === statusFilter.value)
  }

  // Ordenação
  result.sort((a, b) => {
    switch (sortBy.value) {
      case 'name':
        return a.name.localeCompare(b.name)
      case 'updatedAt':
        return new Date(b.updatedAt) - new Date(a.updatedAt)
      case 'version':
        return b.version.localeCompare(a.version)
      default:
        return 0
    }
  })

  return result
})

const totalPages = computed(() => {
  return Math.ceil(filteredApis.value.length / itemsPerPage.value)
})

const paginatedApis = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value
  return filteredApis.value.slice(start, end)
})

// Métodos
const loadApis = async () => {
  loading.value = true
  try {
    apis.value = await getAllApis()
  } catch (error) {
    console.error('Erro ao carregar APIs:', error)
    apis.value = []
  } finally {
    loading.value = false
  }
}

const performSearch = async () => {
  if (!searchQuery.value.trim()) {
    await loadApis()
    return
  }

  loading.value = true
  try {
    apis.value = await searchApis(searchQuery.value)
  } catch (error) {
    console.error('Erro na busca:', error)
  } finally {
    loading.value = false
  }
}

const applyFilters = () => {
  currentPage.value = 1
}

const applySorting = () => {
  currentPage.value = 1
}

const refreshApis = () => {
  loadApis()
}

const viewApi = (apiId) => {
  router.push(`/apis-old/${apiId}`)
}

const showCodeGenerator = (api) => {
  selectedApi.value = api
  selectedEndpoint.value = {
    path: '/example',
    method: 'GET'
  }
  showCodeModal.value = true
}

const viewDocumentation = (api) => {
  router.push(`/apis/${api.id}`)
}

const closeCodeModal = () => {
  showCodeModal.value = false
  selectedApi.value = null
  selectedEndpoint.value = null
}

// Watchers
watch([statusFilter, sortBy], () => {
  currentPage.value = 1
})

// Lifecycle
onMounted(() => {
  loadApis()
})
</script>